import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { HOLD_AND_SPIN_NAME } from './HoldAndSpinBoard';
import type { PayoutTable } from './PayoutTable';
import type { SlotSymbolDef } from './types';

/** A tab across the top of the info board (the Reel Race's Paytable / Items). */
export interface InfoDialogTab {
  label: string;
  active: boolean;
  onTap: () => void;
}

/** The special symbols' rules, as the paytable states them. */
export interface PaytableRules {
  /** Money symbols on one spin that start hold and spin. */
  holdAndSpinTrigger: number;
  /** Scatters anywhere on the reels that win free spins. */
  scatterTrigger: number;
}

interface Row {
  symbol: SlotSymbolDef;
  art: Sprite;
  name: Text;
  cols: [Text, Text, Text];
}

interface Special {
  symbol: SlotSymbolDef;
  art: Sprite;
  line: Text;
}

/** Room above and below the board: its plaque rides half above it. */
const MARGIN = 19;

/**
 * The paytable, on a board (producer 2026-10-14: no plain dialogs, canvas
 * pop-ups layer over everything): each paying symbol with what three, four
 * and five in a row pay at the current bet, then the special symbols with
 * a plain line on what each does, and CLOSE. It fits itself to the screen,
 * so nothing scrolls; tapping the dim closes it too.
 */
export class PaytableDisplay extends BoardDialog {
  private payScale = 1;
  private readonly header: Text;
  private readonly headCols: [Text, Text, Text];
  private readonly rules = new Graphics();
  private readonly rowList: Row[] = [];
  private readonly specialList: Special[] = [];
  private readonly specialsTitle: Text;
  private readonly closeButton: BrassButton;
  private readonly tabBar = new Container();
  /** Every symbol picture, with the alias it shows, to refresh on open. */
  private readonly arts: { sprite: Sprite; alias?: string }[] = [];
  private tabButtons: BrassButton[] = [];
  private isOpen = false;
  private screenH = 1;
  private arranging = false;
  /** Fires when the player closes it (the dim or CLOSE). */
  onCloseRequested: (() => void) | null = null;

  constructor(
    private readonly symbols: readonly SlotSymbolDef[],
    private readonly payoutTable: PayoutTable,
    private bet: number,
    private readonly wildSymbolId: string | null,
    private readonly scatterSymbolId: string | null,
    /** Read on each opening: the slot can switch to server spins after
     *  it's built, and the server's rules differ. */
    private readonly ruleCounts: () => PaytableRules,
    private readonly floor: () => Texture | null,
  ) {
    super({ title: 'PAYTABLE', floor: floor(), maxWidth: 540, titleSize: 18 });
    this.header = new Text({ text: 'IN A ROW', style: uiStyle(12, COLOURS.cream, '600') });
    this.header.anchor.set(0, 0.5);
    this.headCols = ['3', '4', '5'].map((n) => {
      const t = new Text({ text: n, style: displayStyle(16) });
      t.anchor.set(1, 0.5);
      return t;
    }) as [Text, Text, Text];
    this.specialsTitle = new Text({ text: 'SPECIAL SYMBOLS', style: displayStyle(15) });
    this.specialsTitle.anchor.set(0.5, 0);
    this.closeButton = new BrassButton({ label: 'CLOSE', kind: 'wood', size: 16, onTap: () => this.requestClose() });
    this.content.addChild(this.tabBar, this.rules, this.header, ...this.headCols, this.specialsTitle, this.closeButton);
    this.onDimTap = () => this.requestClose();
    this.build();
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; rows: { name: string; pays: string[] }[]; specials: string[] } {
    return {
      open: this.isOpen,
      rows: this.rowList.map((r) => ({ name: r.name.text, pays: r.cols.map((c) => c.text) })),
      specials: this.specialList.map((s) => s.line.text),
    };
  }

  /** The Reel Race shows Paytable and Items as tabs; null for none. */
  setTabs(tabs: ReadonlyArray<InfoDialogTab> | null): void {
    for (const b of this.tabButtons) b.destroy({ children: true });
    this.tabButtons = (tabs ?? []).map(
      (tab) => new BrassButton({ label: tab.label, kind: tab.active ? 'gold' : 'wood', size: 15, onTap: tab.onTap }),
    );
    this.tabBar.addChild(...this.tabButtons);
    this.arrange();
  }

  /** The server's pay scale (rr_v1), so the coins shown are what a line
   *  really pays. 1 is the old full paytable. */
  setPayScale(scale: number): void {
    if (scale === this.payScale) return;
    this.payScale = scale;
    for (const row of this.rowList) this.applyPays(row);
  }

  /** Keep the pays in step with the bet. */
  setBet(bet: number): void {
    if (bet === this.bet) return;
    this.bet = bet;
    for (const row of this.rowList) this.applyPays(row);
    this.arrange();
  }

  layout(viewWidth: number, viewHeight: number): void {
    this.screenH = viewHeight;
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  show(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setFloor(this.floor());
    // Symbol art may have loaded since the board was built, and the rules
    // may have changed (server spins).
    this.refreshArt();
    for (const special of this.specialList) special.line.text = this.specialLine(special.symbol) ?? '';
    this.arrange();
    void this.open();
  }

  hide(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    void this.close();
  }

  toggle(): void {
    if (this.isOpen) this.hide();
    else this.show();
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  /** Kept for the scene's teardown; the board's tweens end with it. */
  destroyPaytable(): void {
    this.isOpen = false;
  }

  private requestClose(): void {
    if (!this.isOpen) return;
    this.hide();
    this.onCloseRequested?.();
  }

  private build(): void {
    for (const symbol of this.symbols) {
      if (symbol.id === 'bonus' || symbol.weight === 0) continue;
      if (this.pays(symbol)) {
        const row: Row = {
          symbol,
          art: this.symbolSprite(symbol),
          name: new Text({ text: (symbol.label ?? symbol.id).toUpperCase(), style: uiStyle(14, COLOURS.cream) }),
          cols: [0, 1, 2].map(() => {
            const t = new Text({ text: '', style: displayStyle(16) });
            t.anchor.set(1, 0.5);
            return t;
          }) as [Text, Text, Text],
        };
        row.name.anchor.set(0, 0.5);
        this.content.addChild(row.art, row.name, ...row.cols);
        this.applyPays(row);
        this.rowList.push(row);
      }
      const line = this.specialLine(symbol);
      if (line) {
        const special: Special = {
          symbol,
          art: this.symbolSprite(symbol),
          line: new Text({ text: line, style: { ...uiStyle(14, COLOURS.cream, '500'), wordWrap: true, lineHeight: 18 } }),
        };
        special.line.anchor.set(0, 0.5);
        this.content.addChild(special.art, special.line);
        this.specialList.push(special);
      }
    }
    this.specialsTitle.visible = this.specialList.length > 0;
  }

  /** True when lines of this symbol pay coins. */
  private pays(symbol: SlotSymbolDef): boolean {
    if (symbol.isMoney || (symbol.gemValue ?? 0) > 0) return false;
    return [3, 4, 5].some((n) => this.payoutTable.multiplier(symbol.id, n) > 0);
  }

  /** What a special symbol does, in one plain line; null for ordinary ones. */
  private specialLine(symbol: SlotSymbolDef): string | null {
    const name = symbol.label ?? symbol.id;
    if (symbol.id === this.wildSymbolId) return `${name} stands in for any symbol to make a line.`;
    if (symbol.id === this.scatterSymbolId) {
      return `${this.ruleCounts().scatterTrigger} or more ${plural(name)} anywhere on the reels win free spins.`;
    }
    if (symbol.isMoney) {
      return `${this.ruleCounts().holdAndSpinTrigger} or more ${plural(name)} on one spin start ${HOLD_AND_SPIN_NAME}: each holds a coin prize.`;
    }
    if (symbol.artefactId) return `${name}: a legendary find for your collection.`;
    if ((symbol.gemValue ?? 0) > 0) return `${name}: ${symbol.gemValue} gems wherever it lands.`;
    return null;
  }

  private applyPays(row: Row): void {
    row.cols.forEach((cell, i) => {
      const multiplier = this.payoutTable.multiplier(row.symbol.id, 3 + i);
      if (multiplier > 0) {
        const coins = this.payScale === 1 ? this.bet * multiplier : Math.max(1, Math.round(this.bet * multiplier * this.payScale));
        cell.text = coins.toLocaleString('en-GB');
        cell.alpha = 1;
      } else {
        cell.text = '-';
        cell.alpha = 0.5;
      }
    });
  }

  private symbolSprite(symbol: SlotSymbolDef): Sprite {
    const sprite = new Sprite();
    sprite.anchor.set(0.5);
    this.arts.push({ sprite, alias: symbol.textureAlias });
    const texture = symbol.textureAlias ? AssetLoader.shared.getTexture(symbol.textureAlias) : null;
    if (texture) sprite.texture = texture;
    return sprite;
  }

  private refreshArt(): void {
    for (const { sprite, alias } of this.arts) {
      const texture = alias ? AssetLoader.shared.getTexture(alias) : null;
      if (texture && sprite.texture !== texture) sprite.texture = texture;
    }
  }

  /** Lay the board out to fit the screen: rows shrink on a short one. */
  private arrange(): void {
    if (this.arranging || !this.closeButton) return;
    this.arranging = true;
    const w = this.contentWidth;
    let y = 0;
    if (this.tabButtons.length > 0) {
      const gap = 8;
      const each = Math.floor((w - gap * (this.tabButtons.length - 1)) / this.tabButtons.length);
      this.tabButtons.forEach((b, i) => {
        b.resize(each, 38);
        b.position.set(i * (each + gap), 0);
      });
      y = 50;
    }
    // Room for the rows once everything else has its share. A narrow
    // screen gets smaller lines under the special symbols.
    const narrow = w < 340;
    const specialsWidth = w - 48;
    for (const s of this.specialList) {
      s.line.style.wordWrapWidth = specialsWidth;
      s.line.style.fontSize = narrow ? 13 : 14;
      s.line.style.lineHeight = narrow ? 16 : 18;
    }
    const specialH = (s: Special) => Math.max(narrow ? 30 : 34, s.line.height + 6);
    const specialsH = this.specialList.length ? 30 + this.specialList.reduce((sum, s) => sum + specialH(s), 0) : 0;
    const fixed = y + 24 + 10 + specialsH + 14 + 48;
    const avail = Math.max(200, this.screenH - this.topInset - MARGIN * 2 - 48);
    const rowH = Math.round(Math.max(24, Math.min(40, (avail - fixed) / Math.max(1, this.rowList.length))));

    // Pays line up in three columns at the right.
    const colGap = Math.round(Math.max(54, Math.min(84, w * 0.16)));
    const colX = [w - 4 - colGap * 2, w - 4 - colGap, w - 4];
    this.header.position.set(rowH + 8, y + 10);
    this.headCols.forEach((t, i) => t.position.set(colX[i]!, y + 10));
    y += 24;
    const lines = this.rules.clear();
    lines.moveTo(0, y).lineTo(w, y).stroke({ color: COLOURS.brass, alpha: 0.45, width: 1 });
    const numSize = rowH >= 34 ? 17 : 15;
    for (const row of this.rowList) {
      const cy = y + rowH / 2;
      fit(row.art, rowH - 6);
      row.art.position.set(rowH / 2, cy);
      row.name.style.fontSize = rowH >= 34 ? 15 : 13;
      row.name.position.set(rowH + 8, cy);
      row.cols.forEach((c, i) => {
        c.style.fontSize = numSize;
        c.position.set(colX[i]!, cy);
      });
      y += rowH;
      lines.moveTo(0, y).lineTo(w, y).stroke({ color: COLOURS.brass, alpha: 0.18, width: 1 });
    }
    y += 10;
    if (this.specialList.length) {
      this.specialsTitle.position.set(w / 2, y);
      y += 30;
      for (const s of this.specialList) {
        const h = specialH(s);
        fit(s.art, narrow ? 28 : 32);
        s.art.position.set(18, y + h / 2);
        s.line.position.set(46, y + h / 2);
        y += h;
      }
    }
    y += 14;
    const bw = Math.min(w, 200);
    this.closeButton.resize(bw, 44);
    this.closeButton.position.set((w - bw) / 2, y);
    y += 48;
    this.setContentHeight(y);
    this.arranging = false;
  }
}

/** Scale a sprite to fit a square of `size`, keeping its shape. */
function fit(sprite: Sprite, size: number): void {
  const tw = sprite.texture.width || 1;
  const th = sprite.texture.height || 1;
  const k = size / Math.max(tw, th);
  sprite.scale.set(k);
}

/** A symbol's name for more than one: Moonflowers, Foxes, Gems. */
export function plural(name: string): string {
  if (/(ss|x|ch|sh)$/i.test(name)) return `${name}es`;
  if (/s$/i.test(name)) return name;
  if (/[^aeiou]y$/i.test(name)) return `${name.slice(0, -1)}ies`;
  return `${name}s`;
}
